import React, { useState, useEffect } from 'react';
import { Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/card';
import { useAuth } from '../../lib/auth.context';
import { fetchPlayerNews, NewsArticle, PlayerNewsParams } from '../../lib/services/playerNewsService';

interface PlayerNewsPanelProps {
  playerId: string;
  playerName?: string;
  onClose?: () => void;
}

export const PlayerNewsPanel: React.FC<PlayerNewsPanelProps> = ({ 
  playerId, 
  playerName,
  onClose 
}) => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  
  const { getToken } = useAuth();
  const pageSize = 5;
  
  useEffect(() => {
    const loadNews = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const token = getToken();
        if (!token) {
          throw new Error('Not authenticated');
        }
        
        const params: PlayerNewsParams = {
          limit: pageSize,
          offset: (page - 1) * pageSize,
          sort_by: 'relevance',
          sort_order: 'desc'
        };
        
        const response = await fetchPlayerNews(playerId, token, params);
        
        setArticles(response.items);
        setTotalCount(response.totalCount);
        setTotalPages(Math.ceil(response.totalCount / pageSize));
      } catch (err) {
        console.error('Error fetching player news:', err);
        setError(err instanceof Error ? err.message : 'Error loading news articles');
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadNews();
  }, [playerId, page, getToken]);
  
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  const handleArticleClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Date unknown';
    
    try {
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Date unknown';
      }
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return 'Date unknown';
    }
  };
  
  return (
    <Card className="w-full bg-transparent border-0 shadow-none">
      <CardHeader className="px-0 pt-0">
        <CardTitle className="flex justify-between items-center text-[#CECFD2]">
          <span>{playerName ? `${playerName} - News` : 'Player News'}</span>
          {onClose && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="h-8 px-2 rounded-full text-[#CECFD2] hover:bg-[#26222E]"
            >
              Close
            </Button>
          )}
        </CardTitle>
        {totalCount > 0 && (
          <CardDescription className="text-[#CECFD2]/70">
            {totalCount} {totalCount === 1 ? 'article' : 'articles'} found
          </CardDescription>
        )}
      </CardHeader>
      
      <CardContent className="px-0">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#4E46FF]" />
            <span className="ml-2 text-[#CECFD2]">Loading news articles...</span>
          </div>
        ) : error ? (
          <div className="py-8 text-center">
            <p className="text-red-500 mb-2">Error loading news articles</p>
            <p className="text-sm text-[#CECFD2]/70">{error}</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-[#CECFD2]/70">No news articles found for this player.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((article) => (
              <div 
                key={article.id} 
                className="p-4 border border-gray-500 rounded-lg hover:bg-[#26222E] cursor-pointer transition-colors"
                onClick={() => handleArticleClick(article.article_url)}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-grow">
                    <h3 className="text-base font-medium text-[#CECFD2] group-hover:text-[#4E46FF] mb-1">
                      {article.headline}
                    </h3>
                    {article.snippet && (
                      <p className="text-sm text-[#CECFD2] mb-2 line-clamp-2">
                        {article.snippet}
                      </p>
                    )}
                    <div className="flex justify-between items-center text-xs text-[#CECFD2]/70">
                      <span>{article.source_name}</span>
                      <span>{formatDate(article.publication_date)}</span>
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-[#CECFD2] flex-shrink-0 mt-1" />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      
      {!loading && !error && totalPages > 1 && (
        <CardFooter className="flex justify-center pt-2 pb-4 px-0">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="h-8 w-8 p-0 border-gray-700 text-[#CECFD2] hover:bg-[#26222E] hover:text-[#CECFD2] disabled:opacity-50"
            >
              &lt;
            </Button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                // Show pages around the current page
                let pageNum = page;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    className={`h-8 w-8 p-0 ${
                      pageNum === page 
                        ? 'bg-[#4E46FF] hover:bg-[#3d37cc] text-white' 
                        : 'border-gray-700 text-[#CECFD2] hover:bg-[#26222E] hover:text-[#CECFD2]'
                    }`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="h-8 w-8 p-0 border-gray-700 text-[#CECFD2] hover:bg-[#26222E] hover:text-[#CECFD2] disabled:opacity-50"
            >
              &gt;
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  );
};